import { useState } from "react";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

/* ==========================================================================
   SHOWCASE GRID — 12 IMAGES — UPDATE URLS AFTER UPLOAD TO 10WEB
   ==========================================================================
   Recommended sizes:
   - Large tiles (col-span-2 row-span-2): 1200×900 WebP @82 (~120KB)
   - Medium tiles (col-span-2 OR row-span-2): 800×900 or 1200×600 (~80KB)
   - Small tiles: 600×600 WebP @82 (~50KB)
   ========================================================================== */

type Category = "all" | "sectional" | "roller" | "insulated" | "repairs";
const IMAGES: {
  src: string;
  alt: string;
  category: Exclude<Category, "all">;
  suburb: string;
  title: string;
  span: string; // tailwind-style spans
}[] = [{
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/repair-hero-desktop-1.webp?v=ttougkla5nh",
  alt: "Sectional garage door install in Bondi, Sydney — Gold Garage Door",
  category: "sectional",
  suburb: "Bondi",
  title: "B&D Panelift in Monument",
  span: "col-span-2 row-span-2"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/image4-hero-tablet.webp?v=xhqkx9y5p2p",
  alt: "Insulated Steel-Line Therma-Lux door in Drummoyne",
  category: "insulated",
  suburb: "Drummoyne",
  title: "Therma-Lux Premium",
  span: "col-span-1 row-span-1"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_7558-scaled.jpg?v=j5t9v4s2sva",
  alt: "Roller garage door install in Parramatta",
  category: "roller",
  suburb: "Parramatta",
  title: "ColorBond roller",
  span: "col-span-1 row-span-1"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_7240-scaled.webp?v=tveetgykv",
  alt: "Spring & cable repair in Hornsby home",
  category: "repairs",
  suburb: "Hornsby",
  title: "Spring & cable repair",
  span: "col-span-2 row-span-1"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_7603-scaled.jpg?v=izg1vyadzr9",
  alt: "Sectional door installation in Manly, Sydney",
  category: "sectional",
  suburb: "Manly",
  title: "Coastal sectional",
  span: "col-span-1 row-span-2"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_0034-scaled.webp?v=bx2v5xyiwil",
  alt: "Insulated workshop door in Penrith — Western Sydney",
  category: "insulated",
  suburb: "Penrith",
  title: "Workshop upgrade",
  span: "col-span-1 row-span-1"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/ecb5a9d3-0201-404c-b545-d75f7e3bbef0.webp",
  alt: "Roller door in Cronulla coastal home",
  category: "roller",
  suburb: "Cronulla",
  title: "Beachside roller",
  span: "col-span-1 row-span-1"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/technician-garage-door-repair-768-1.webp?v=5o4hy30fwkq",
  alt: "Motor & opener repair in Lane Cove",
  category: "repairs",
  suburb: "Lane Cove",
  title: "Motor swap",
  span: "col-span-1 row-span-1"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/05/9ada9b93-909d-463a-a8f5-c673b2a6de54.webp?v=u3o4q4gncu",
  alt: "Insulated double garage door in Castle Hill",
  category: "insulated",
  suburb: "Castle Hill",
  title: "Double-insulated",
  span: "col-span-2 row-span-2"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/image2-hero-mobile.webp?v=8dxzg7qq8ds",
  alt: "Sectional garage door installed in Coogee",
  category: "sectional",
  suburb: "Coogee",
  title: "Smooth-skin sectional",
  span: "col-span-1 row-span-1"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_7556-scaled.jpg?v=u4gvnq3ff9g",
  alt: "Roller door installation in Eastwood",
  category: "roller",
  suburb: "Eastwood",
  title: "Compact roller",
  span: "col-span-1 row-span-1"
}, {
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_6361-scaled.webp?v=d1udmu7n6i",
  alt: "Panel restoration in Mosman heritage home",
  category: "repairs",
  suburb: "Mosman",
  title: "Heritage panel restore",
  span: "col-span-2 row-span-1"
}];
const FILTERS: {
  key: Category;
  label: string;
  count: number;
}[] = [{
  key: "all",
  label: "All work",
  count: IMAGES.length
}, {
  key: "sectional",
  label: "Sectional",
  count: IMAGES.filter(i => i.category === "sectional").length
}, {
  key: "roller",
  label: "Roller",
  count: IMAGES.filter(i => i.category === "roller").length
}, {
  key: "insulated",
  label: "Insulated",
  count: IMAGES.filter(i => i.category === "insulated").length
}, {
  key: "repairs",
  label: "Repairs",
  count: IMAGES.filter(i => i.category === "repairs").length
}];
const CATEGORY_LABELS: Record<Exclude<Category, "all">, string> = {
  sectional: "Sectional",
  roller: "Roller",
  insulated: "Insulated",
  repairs: "Repair"
};
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
export default function GalleryShowcase() {
  const [filter, setFilter] = useState<Category>("all");
  const filtered = filter === "all" ? IMAGES : IMAGES.filter(i => i.category === filter);
  return <section id="showcase" className="relative overflow-hidden scroll-mt-24" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 right-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(90deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 left-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(270deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-10 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              04 · The Showcase
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>Twelve doors,<br /><span data-ast-id="f:/src/pages/Gallery/sections/GarageGallery.tsx|sig:f5xdh7" data-ast-start="8668" data-ast-end="8759" style={{
            color: 'rgb(154, 100, 0)'
          }}>twelve Sydney suburbs</span></h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: INK_SOFT,
          maxWidth: "640px"
        }}>
            A slice of recent work — filter by door type to see what your
            install might look like, in your kind of home.
          </p>
        </div>

        {/* Filter chips */}
        <div className="mb-8 flex flex-wrap gap-2.5">
          {FILTERS.map(f => {
          const active = filter === f.key;
          return <button key={f.key} type="button" onClick={() => setFilter(f.key)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all" style={{
            backgroundColor: active ? NAVY : "transparent",
            color: active ? "#ffffff" : INK,
            border: `1.5px solid ${active ? NAVY : "rgba(8,20,56,0.15)"}`,
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: "13px",
            letterSpacing: "-0.005em",
            boxShadow: active ? `0 4px 16px ${NAVY}33` : "none"
          }}>
                {f.label}
                <span className="inline-flex items-center justify-center rounded-full" style={{
              minWidth: "22px",
              height: "20px",
              padding: "0 6px",
              backgroundColor: active ? `${GOLD}` : "rgba(8,20,56,0.08)",
              color: active ? NAVY : INK_SOFT,
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "11px"
            }}>
                  {f.count}
                </span>
              </button>;
        })}
        </div>

        {/* Bento grid */}
        <div className="grid gap-3" style={{
        gridTemplateColumns: "repeat(4, 1fr)",
        gridAutoRows: "180px"
      }}>
          {filtered.map((img, i) => <a key={img.src} href="#contact-us" className={`relative rounded-xl overflow-hidden group ${img.span}`} style={{
          backgroundColor: "#cfcabc",
          boxShadow: "0 6px 20px rgba(8,20,56,0.12)",
          border: `1px solid rgba(8,20,56,0.06)`,
          textDecoration: "none"
        }}>
              <img src={img.src} alt={img.alt} loading="lazy" draggable={false} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]" />

              {/* Default subtle gradient (always visible) */}
              <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: "linear-gradient(180deg, transparent 50%, rgba(8,20,56,0.55) 100%)"
          }} />

              {/* Hover full gradient */}
              <div aria-hidden className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
            background: "linear-gradient(180deg, rgba(8,20,56,0.15) 0%, rgba(8,20,56,0.92) 100%)"
          }} />

              {/* Category pill (top-left) */}
              <div className="absolute top-3 left-3" style={{
            padding: "4px 10px",
            borderRadius: "999px",
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontSize: "9px",
            fontWeight: 900,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)"
          }}>
                {CATEGORY_LABELS[img.category]}
              </div>

              {/* Bottom info */}
              <div className="absolute bottom-0 left-0 right-0 p-3.5 text-white">
                <div className="flex items-center gap-1.5 mb-1 opacity-100 group-hover:opacity-100 transition-opacity">
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <MapPin size={11} color="#ffffff" strokeWidth={2.5} />
                  </span>
                  <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "-0.005em"
              }}>
                    {img.suburb}
                  </span>
                </div>

                <h4 className="opacity-0 group-hover:opacity-100 transition-all duration-300" style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "14px",
              lineHeight: 1.2,
              letterSpacing: "-0.015em",
              maxHeight: "0",
              transform: "translateY(8px)",
              transitionProperty: "opacity, transform, max-height"
            }}>
                  {img.title}
                </h4>
              </div>

              {/* Hover arrow badge */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300" style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: "translateY(-4px)",
            boxShadow: `0 4px 12px ${NAVY}55`
          }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ArrowRight size={13} color={NAVY} strokeWidth={2.75} />
                </span>
              </div>
            </a>)}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && <div className="rounded-xl p-12 text-center" style={{
        backgroundColor: CREAM_DEEP,
        border: `1px dashed ${GOLD}55`
      }}>
            <p style={{
          fontFamily: FONT_BODY,
          fontSize: "15px",
          color: INK_SOFT
        }}>
              No photos in this category yet — try another filter.
            </p>
          </div>}

        {/* Bottom CTA strip */}
        <div className="mt-12 rounded-xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" style={{
        backgroundColor: CREAM_DEEP,
        border: `1px dashed ${GOLD}55`
      }}>
          <div>
            <div style={{
            fontFamily: FONT_HEAD,
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: GOLD_DARK,
            marginBottom: "6px"
          }}>
              See yours next
            </div>
            <h4 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "1.15rem",
            color: NAVY,
            letterSpacing: "-0.01em",
            lineHeight: 1.3
          }}>
              Want a door like one of these on your driveway?
            </h4>
          </div>
          <a href="tel:0485000933" className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "13px",
          boxShadow: `0 6px 18px ${NAVY}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={14} strokeWidth={2.5} />
            </span>
            Book your install
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={13} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>
    </section>;
}